import { TrendingUp, TrendingDown } from "lucide-react";

// Helper function to format price with dynamic decimal places
const formatPrice = (price) => {
  if (price === null || price === undefined || price === 0) return '$0.00';
  if (price < 0.0001) {
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 8, maximumFractionDigits: 10 })}`;
  } else if (price < 1) {
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 6 })}`;
  } else if (price < 100) {
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
  } else {
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
};

const PriceCard = ({ coin, data }) => {
  // Handle null/undefined data gracefully
  const price = data?.price ?? 0;
  const priceChange = data?.price_change_24h ?? 0;
  const volume = data?.volume_24h ?? 0;
  const marketCap = data?.market_cap ?? 0;
  const isPositive = priceChange >= 0;

  // Check if data is unavailable
  const isDataUnavailable = price === 0 || price === null;

  return (
    <div className="glass-card rounded-xl p-6 border-white/10 hover:border-primary/50 transition-all duration-300 hover:-translate-y-1 animate-fade-in-up" data-testid={`price-card-${coin.toLowerCase()}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-xs text-muted-foreground mb-1">Price</div>
          <div className="text-sm font-semibold text-muted-foreground">{coin}/USD</div>
        </div>
        <div className={`p-2 rounded-lg ${
          isPositive ? "bg-[#00FF94]/10" : "bg-[#FF0055]/10"
        }`}>
          {isPositive ? (
            <TrendingUp className="w-5 h-5 text-[#00FF94]" />
          ) : (
            <TrendingDown className="w-5 h-5 text-[#FF0055]" />
          )}
        </div>
      </div>

      {isDataUnavailable ? (
        <div className="space-y-3">
          <div className="text-center py-4">
            <div className="text-sm text-muted-foreground">
              ⚠️ Price data unavailable
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              This coin may not be supported by CMC API
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <div className="text-3xl font-bold text-data" data-testid={`${coin.toLowerCase()}-price`}>
              {formatPrice(price)}
            </div>
            <div className={`text-sm font-semibold mt-1 ${
              isPositive ? "text-[#00FF94]" : "text-[#FF0055]"
            }`} data-testid={`${coin.toLowerCase()}-change`}>
              {isPositive ? "+" : ""}{priceChange.toFixed(2)}%
            </div>
          </div>

          <div className="border-t border-white/10 pt-3 space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Volume (24h)</span>
              <span className="text-data font-semibold" data-testid={`${coin.toLowerCase()}-volume`}>
                {volume >= 1e9 
                  ? `$${(volume / 1e9).toFixed(2)}B`
                  : volume >= 1e6
                  ? `$${(volume / 1e6).toFixed(2)}M`
                  : `$${volume.toLocaleString()}`
                }
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Market Cap</span>
              <span className="text-data font-semibold" data-testid={`${coin.toLowerCase()}-marketcap`}>
                {marketCap >= 1e9 
                  ? `$${(marketCap / 1e9).toFixed(2)}B`
                  : marketCap >= 1e6
                  ? `$${(marketCap / 1e6).toFixed(2)}M`
                  : marketCap > 0
                  ? `$${marketCap.toLocaleString()}`
                  : 'N/A'
                }
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceCard;